import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Palette } from 'lucide-react';

/**
 * Development-only component for switching between tenant configurations
 * Allows testing different themes and branding without restarting the server
 */
export function TenantSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  
  // Fetch available tenants and current tenant
  // This will fail in production mode, hiding the component
  // Only enable in development to prevent 403 errors in production
  const { data: tenantData, isLoading, error } = trpc.config.listTenants.useQuery(undefined, {
    enabled: import.meta.env.DEV,
    retry: false,
  });
  
  // Hide if backend blocks tenant switching (production mode)
  if (error) {
    return null;
  }
  
  // Mutation to switch tenant
  const switchTenant = trpc.config.switchTenant.useMutation({
    onSuccess: () => {
      // Reload the page to apply new tenant config
      window.location.reload();
    },
  });

  // Mutation to reset to environment tenant
  const resetTenant = trpc.config.resetTenant.useMutation({
    onSuccess: () => {
      window.location.reload();
    },
  });

  const handleSwitchTenant = (tenantId: string) => {
    switchTenant.mutate({ tenantId });
  };

  const handleReset = () => {
    resetTenant.mutate();
  };

  if (isLoading || !tenantData) {
    return null;
  }

  return (
    <>
      {/* Floating toggle button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center"
          title="Tenant Switcher (Dev Only)"
        >
          <Palette className="h-5 w-5" />
        </button>
      )}

      {/* Switcher panel */}
      {isOpen && (
        <Card className="fixed bottom-4 right-4 z-50 w-80 shadow-xl">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                <CardTitle className="text-sm">Tenant Switcher</CardTitle>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                ✕
              </button>
            </div>
            <CardDescription className="text-xs">
              Development mode only - Switch between tenant configs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Current Tenant</label>
              <div className="text-xs text-muted-foreground bg-muted px-3 py-2 rounded-md">
                {tenantData.current}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Switch To</label>
              <Select onValueChange={handleSwitchTenant}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant..." />
                </SelectTrigger>
                <SelectContent>
                  {tenantData.tenants.map((tenant) => (
                    <SelectItem key={tenant} value={tenant}>
                      {tenant}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={resetTenant.isPending}
              className="w-full"
            >
              <RefreshCw className="h-3 w-3 mr-2" />
              Reset to Environment
            </Button>

            {(switchTenant.isPending || resetTenant.isPending) && (
              <div className="text-xs text-center text-muted-foreground">
                Switching tenant...
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
